import {
  Box,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { fieldSx } from "./sections/sectionStyles";

import { InfoOutlinedIcon } from "@/icons";
import OrionSwitch from "@/orion/OrionComponents/OrionSwitch";

export interface SmartAudienceSectionProps {
  /** Smart Audience toggle state, also sent to `/reach` as `reachOptimization`. */
  reachOptimization: boolean;
  onReachOptimizationChange: (value: boolean) => void;
  /** Trim percentage (1–99); the slider is shown only while the toggle is on. */
  reachOptimizationPercentage: number;
  onReachOptimizationPercentageChange: (value: number) => void;
  /** Read-only once the broadcast leaves DRAFT (mirrors the other edits). */
  disabled?: boolean;
}

/**
 * The Smart Audience reach-optimization toggle ("Exclude Unengaged Profiles").
 * When on, a percentage slider (1–99, mirroring the A/B Test Size control) sets
 * how much of the lowest-engagement audience to trim. Gated by the Smart
 * Audience feature flag at the call site. The reach estimate is rendered
 * separately by {@link AudienceReachBanner}.
 */
export function SmartAudienceSection({
  reachOptimization,
  onReachOptimizationChange,
  reachOptimizationPercentage,
  onReachOptimizationPercentageChange,
  disabled,
}: SmartAudienceSectionProps) {
  return (
    <Stack
      gap={3}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        px: 4,
        py: 3,
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={3}
        data-testid="smart-audience-toggle"
      >
        <OrionSwitch
          checked={reachOptimization}
          disabled={disabled}
          onChange={(_event, next) => onReachOptimizationChange(next)}
          inputProps={{ "aria-label": "Enable Smart Audience" }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Exclude Unengaged Profiles
        </Typography>
        <Tooltip
          title="Removes the profiles least likely to engage, ranked by their predicted engagement score for this medium. Profiles too new to score are always included; if scores are unavailable at send time, none are removed."
          arrow
        >
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }}
            />
          </Box>
        </Tooltip>
      </Stack>

      {reachOptimization && (
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          data-testid="smart-audience-percentage"
        >
          <TextField
            value={reachOptimizationPercentage}
            onChange={(e) =>
              onReachOptimizationPercentageChange(Number(e.target.value))
            }
            type="number"
            size="small"
            disabled={disabled}
            inputProps={{
              min: 1,
              max: 99,
              "aria-label": "Percent of unengaged profiles to exclude",
            }}
            sx={(theme) => ({ width: 100, ...fieldSx(theme) })}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
          <Slider
            aria-label="Percent of unengaged profiles to exclude"
            value={reachOptimizationPercentage}
            onChange={(_, v) => onReachOptimizationPercentageChange(v)}
            min={1}
            max={99}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.primary"
            sx={{ minWidth: 40, textAlign: "right" }}
          >
            {reachOptimizationPercentage}%
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
